import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  membershipDiscountPayment,
  membershipDiscountPaymentDelete,
  setUsingWallet,
} from '@/actions/purchase';
import { walletSearchDiscount } from '@/actions/search';
import { updateWalletMoney } from '@/actions/loyalty';
import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import parseQueryString from 'utils/queryString/parseQueryString';
import {
  loyaltyApplyTracker,
  loyaltyRemoveTracker,
  loyaltyModifyTracker,
} from 'metrics/user-analytics/loyalty';
import Cookies from 'js-cookie';
import store from '@/store';
import LoyaltyProgramsContext from './LoyaltyProgramsContext';
import useLoyaltyWidgetSubscriptions from '../hooks/useLoyaltyWidgetSubscriptions';
import { getWalletTypeForPurchase, isServiceAllowed } from '../../utils/loyalty';

/**
 * Quiet interval between checks: Doters (siemprePlus) / CostaPass (costapass) can update after search.
 * Used only for quick purchase visibility (not Reserhub).
 */
const QUICK_PURCHASE_LOYALTY_QUIET_MS = 500;
/**
 * Max repeats while Doters/CostaPass session may still hydrate (cookie present, Redux not yet user/profile).
 */
const QUICK_PURCHASE_LOYALTY_AMBIGUOUS_MAX_POLLS = 36;

/**
 * Ambiguous = widget may still push user/profile after `requested` + empty Redux.
 * `requested && !user` alone is also the stable anonymous shape — only treat as ambiguous if the
 * legacy session cookie hints a session may load (`token` / `costapassToken`, same as reducers).
 * @returns {{ ambiguousEither: boolean, ambiguousDoters: boolean, ambiguousCosta: boolean }}
 */
function getQuickPurchaseLoyaltyAmbiguitySnapshot() {
  const state = store.getState();
  const wl = state.whitelabelConfig?.features || {};
  const dotersOn = Boolean(wl.DOTERS_ENABLED);
  const costaOn = Boolean(wl.COSTAPASS_ENABLED);
  const sp = state.siemprePlus;
  const cp = state.costapass;
  const hasSiemprePlusToken = Boolean(Cookies.get('token'));
  const hasCostapassToken = Boolean(Cookies.get('costapassToken'));
  const ambiguousDoters =
    dotersOn && Boolean(sp.get('requested')) && !sp.get('user') && hasSiemprePlusToken;
  const ambiguousCosta =
    costaOn && Boolean(cp.get('requested')) && !cp.get('profile') && hasCostapassToken;
  return {
    ambiguousDoters,
    ambiguousCosta,
    ambiguousEither: ambiguousDoters || ambiguousCosta,
  };
}

const propTypes = {
  children: PropTypes.node.isRequired,
};

const LoyaltyProgramsProvider = ({ children }) => {
  useLoyaltyWidgetSubscriptions();
  const { search: queryParams } = useLocation();
  const { coupon: couponCodeQueryParam } = parseQueryString(queryParams);
  const dispatch = useDispatch();
  const purchase = usePurchase();
  const costaPass = useSelector((state) => state.costapass.toJS());
  const siemprePlus = useSelector((state) => state.siemprePlus.toJS());
  const siemprePlusRequested = useSelector((state) => state.siemprePlus.get('requested'));
  const siemprePlusHasUser = useSelector((state) => Boolean(state.siemprePlus.get('user')));
  const siemprePlusIsUpdated = useSelector((state) => state.siemprePlus.get('isUpdated'));
  const costapassRequested = useSelector((state) => state.costapass.get('requested'));
  const costapassHasProfile = useSelector((state) => Boolean(state.costapass.get('profile')));
  const search = useSelector((state) => state.search.toJS());
  const costaPassUserIsLoggedIn = Boolean(costaPass?.profile);
  const costaPassUserIsRequested = costaPass.requested;
  const mainUserIsLoggedIn = Boolean(siemprePlus.user);
  // eslint-disable-next-line prettier/prettier
  const mainUserIsRequested = siemprePlus.requested;
  const dotersOrSiemprePlusUser = useSelector((state) => state.siemprePlus.getIn(['user']));
  const originId = useSelector((state) => state.search.get('originId'));
  const destinationId = useSelector((state) => state.search.get('destinationId'));
  const departureDate = useSelector((state) => state.search.get('departureDate'));
  const returnDate = useSelector((state) => state.search.get('returnDate'));
  const environment = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();

  const quickPurchaseLoyaltySettlingNeeded =
    Boolean(features.DOTERS_ENABLED) || Boolean(features.COSTAPASS_ENABLED);

  const [isQuickPurchaseLoyaltySettled, setIsQuickPurchaseLoyaltySettled] = useState(
    !quickPurchaseLoyaltySettlingNeeded,
  );

  const quickPurchaseLoyaltyTimerRef = useRef(null);

  /**
   * Quick purchase only: decide when loyalty state is "settled" enough to evaluate whether to show
   * quick purchase (TripItem / banner). Does not change Reserhub or `userIsLoggedInWithAnyLoyaltyProgram`.
   *
   * Why: After search, the Doters/CostaPass widgets can dispatch `requested: true` with an empty
   * user/profile in Redux before the real session hydrates — that would flash quick purchase on then off.
   * We hold `isQuickPurchaseLoyaltySettled` false until we are confident we are not in that middle state.
   *
   * What "ambiguous" means here: "We might still get a Doters user or a CostaPass profile in Redux
   * soon." It is NOT the same as `requested && !user` alone — that shape is also the stable guest
   * case when Doters is enabled. Ambiguity therefore also requires the matching
   * session cookie (`token` / `costapassToken`);.
   *
   * Flow: On relevant Redux changes, reset to unsettled, wait `QUICK_PURCHASE_LOYALTY_QUIET_MS`,
   * then re-read fresh state from the store. If still ambiguous, reschedule up to
   * `QUICK_PURCHASE_LOYALTY_AMBIGUOUS_MAX_POLLS` times; otherwise set settled so consumers can show/hide QP.
   *
   * Examples for developers:
   * - Ambiguous (keep polling): `DOTERS_ENABLED`, `siemprePlus.requested === true`, no `user` in Redux,
   *   but cookie `token` exists → session may still load into Redux.
   * - Not ambiguous (settle immediately after quiet window): same Redux as guest, **no** `token` →
   *   not waiting for Doters hydration; do not burn the full poll budget on anonymous traffic.
   * - Not ambiguous: `user` or `profile` present → snapshot is not ambiguous; next tick settles.
   */
  useEffect(() => {
    if (!quickPurchaseLoyaltySettlingNeeded) {
      setIsQuickPurchaseLoyaltySettled(true);
      return undefined;
    }

    // Doters or CostaPass is on: assume unsettled until the quiet window + ambiguity check pass.
    setIsQuickPurchaseLoyaltySettled(false);

    let cancelled = false; // Ignore pending timeouts after cleanup or when deps re-run.

    // eslint-disable-next-line jsdoc/require-jsdoc
    const clearTimer = () => {
      if (quickPurchaseLoyaltyTimerRef.current != null) {
        window.clearTimeout(quickPurchaseLoyaltyTimerRef.current);
        quickPurchaseLoyaltyTimerRef.current = null;
      }
    };

    // Fresh store + cookies each call — avoids stale closures if Redux updates between timeouts.
    const isLoyaltyAmbiguousForQuickPurchase = () => {
      return getQuickPurchaseLoyaltyAmbiguitySnapshot().ambiguousEither;
    };

    const scheduleNextCheck = (pollsLeft) => {
      clearTimer();
      quickPurchaseLoyaltyTimerRef.current = window.setTimeout(() => {
        if (cancelled) return;
        const stillAmbiguous = isLoyaltyAmbiguousForQuickPurchase();
        // Still in "might hydrate session" state and budget left → wait another quiet window and re-check.
        if (stillAmbiguous && pollsLeft > 0) {
          scheduleNextCheck(pollsLeft - 1);
        } else {
          // Either no longer ambiguous, or polls exhausted — allow TripItem/banner to evaluate QP flags.
          setIsQuickPurchaseLoyaltySettled(true);
          quickPurchaseLoyaltyTimerRef.current = null;
        }
      }, QUICK_PURCHASE_LOYALTY_QUIET_MS);
    };

    scheduleNextCheck(QUICK_PURCHASE_LOYALTY_AMBIGUOUS_MAX_POLLS);

    return () => {
      cancelled = true;
      clearTimer(); // Stop polling chain when effect re-runs or provider unmounts.
    };
  }, [
    quickPurchaseLoyaltySettlingNeeded,
    siemprePlusRequested,
    siemprePlusHasUser,
    siemprePlusIsUpdated,
    costapassRequested,
    costapassHasProfile,
  ]);

  const selectedLoyaltyProgram = getWalletTypeForPurchase();
  const { availableWallets = [] } = purchase;
  const isValidWalletType =
    selectedLoyaltyProgram && availableWallets.includes(selectedLoyaltyProgram);
  const commonWalletProperties = {
    minUsableBalance: purchase.walletMinimumBalanceToBeUsed || 0,
    maxUsableBalance: purchase.maxUsableBalance,
    isUpdating: purchase.updatingWallet,
    wantsFlatFare: purchase.wantsFlatFare,
    usedBalance: purchase.walletBalanceUsed,
    usedPoints: Number(purchase.walletPointsUsed),
    minPointsToUse: purchase.walletMinimumPointsToBeUsed,
  };
  const costaPasProfile = costaPass?.profile || {};
  const loyaltyProgramsProfiles = {
    siemprePlus: dotersOrSiemprePlusUser?.toJS() || {},
    doters: dotersOrSiemprePlusUser?.toJS() || {},
    costapass: costaPasProfile,
  };
  const costaPassBalance = costaPasProfile?.balance || 0;
  const costaPassValues = {
    userIsLoggedIn: costaPassUserIsLoggedIn,
    userIsRequested: features.COSTAPASS_ENABLED ? costaPassUserIsRequested : true,
    profile: loyaltyProgramsProfiles.costapass,
    wallet: {
      ...commonWalletProperties,
      balance: costaPassBalance,
      maxUsableBalance:
        purchase.maxUsableBalance > costaPassBalance ? costaPassBalance : purchase.maxUsableBalance,
    },
    config: {
      ...(environment.travelpassLoyaltyConfig || {}),
    },
  };

  const dotersValues = {
    userIsLoggedIn: mainUserIsLoggedIn,
    userIsRequested: mainUserIsRequested,
    profile: loyaltyProgramsProfiles.doters,
    wallet: {
      ...commonWalletProperties,
    },
  };

  const { walletTypeDiscount } = search;
  const tripsWithWalletDiscount = search.discountedTripsByCoupon
    ? search.discountedTripsByCoupon[walletTypeDiscount] || []
    : [];

  useEffect(() => {
    if (!originId || mainUserIsLoggedIn || couponCodeQueryParam) return;
    if (costaPassUserIsLoggedIn) {
      dispatch(walletSearchDiscount('ewallet'));
    }
  }, [
    originId,
    departureDate,
    returnDate,
    destinationId,
    costaPassUserIsLoggedIn,
    mainUserIsLoggedIn,
    couponCodeQueryParam,
    dispatch,
  ]);

  /**
   * Function that validates if the loyalty program should be validated on exchange based on the feature flag
   * @param {*} loyalty - loyalty program to validate
   * @returns - boolean
   */
  const shouldValidateOnExchange = (loyalty) => {
    const { VALIDATE_LOYALTY_ON_EXCHANGE } = features;
    if (!VALIDATE_LOYALTY_ON_EXCHANGE || !VALIDATE_LOYALTY_ON_EXCHANGE.length) return false;
    return VALIDATE_LOYALTY_ON_EXCHANGE.includes(loyalty);
  };

  // CostaPass Methods

  // used to differentiate between costapass & travelpass for ui display naming
  const walletPrefix = environment.travelpassLoyaltyConfig?.prefix || 'costapass';
  const onApplyCostaPassPointsDiscount = (pointsToApply) => {
    const { costapassId, token: costaPassToken } = costaPass.profile;
    const pointsToApplyWithBackendFormat = Number(pointsToApply).toFixed(2).toString();
    dispatch(
      membershipDiscountPayment(
        purchase.token,
        {
          membership_id: costapassId,
          membership_token: costaPassToken,
          amount: pointsToApplyWithBackendFormat,
          type: 'costapass',
        },
        walletPrefix,
      ),
    );
    loyaltyApplyTracker({
      wallet: 'costapass',
      purchaseToken: purchase.token,
      amount: pointsToApplyWithBackendFormat,
    });
  };

  const onRemoveCostaPassPointsDiscount = () => {
    dispatch(membershipDiscountPaymentDelete(purchase.token, walletPrefix));
    loyaltyRemoveTracker({
      wallet: 'costapass',
      purchaseToken: purchase.token,
    });
  };

  const onRefreshCostaPassWalletBalance = () => {
    const { token: costaPassToken } = costaPass.profile;
    dispatch(updateWalletMoney('ewallet', costaPassToken, purchase.token));
  };

  // End CostaPass Methods

  // Doters Methods
  const onApplyDotersPointsDiscount = ({ pointsToApply, walletPointsUsed }) => {
    const updating = walletPointsUsed > 0;
    dispatch(setUsingWallet(purchase.token, true, pointsToApply, updating));
    const trackerPayload = {
      wallet: 'doters',
      purchaseToken: purchase.token,
      amount: pointsToApply,
    };

    // If the operation is about updating or modifying the points, we need to send a different tracker
    if (updating) {
      loyaltyModifyTracker(trackerPayload);
    } else {
      loyaltyApplyTracker(trackerPayload);
    }
  };

  const onRemoveDotersPointsDiscount = () => {
    dispatch(setUsingWallet(purchase.token, false));
    loyaltyRemoveTracker({
      wallet: 'doters',
      purchaseToken: purchase.token,
    });
  };

  // End Doters Methods

  const { departs, returns } = purchase;
  const isDotersServiceAvailable = isServiceAllowed({
    departs,
    returns,
    allowedServices: features.DOTERS_ALLOWED_SERVICES,
  });

  const contextValue = {
    selectedLoyaltyProgram: isValidWalletType ? selectedLoyaltyProgram : null,
    selectedLoyaltyProgramProfile: isValidWalletType
      ? loyaltyProgramsProfiles[selectedLoyaltyProgram]
      : undefined,
    userIsLoggedInWithAnyLoyaltyProgram: costaPassUserIsLoggedIn || mainUserIsLoggedIn,
    isQuickPurchaseLoyaltySettled,
    costaPass: {
      ...costaPassValues,
      onApplyPointsDiscount: onApplyCostaPassPointsDiscount,
      onRemovePointsDiscount: onRemoveCostaPassPointsDiscount,
      onRefreshWalletBalance: onRefreshCostaPassWalletBalance,
    },
    doters: {
      ...dotersValues,
      onApplyPointsDiscount: onApplyDotersPointsDiscount,
      onRemovePointsDiscount: onRemoveDotersPointsDiscount,
      isServiceAvailable: isDotersServiceAvailable,
    },
    tripsWithWalletDiscount,
    thereAreTripsWithWalletDiscount: tripsWithWalletDiscount.length > 0,
    loggedInWithMultiplePrograms: costaPassUserIsLoggedIn && mainUserIsLoggedIn,
    shouldValidateOnExchange,
  };

  return (
    <LoyaltyProgramsContext.Provider value={contextValue}>
      {children}
    </LoyaltyProgramsContext.Provider>
  );
};

LoyaltyProgramsProvider.propTypes = propTypes;

export default LoyaltyProgramsProvider;
